import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Card, 
  Space, 
  Modal, 
  Form, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Alert,
  Spin,
  Tooltip,
  Popconfirm,
  message,
  Avatar,
  Statistic,
  DatePicker,
  Divider,
  Badge
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  CloseOutlined,
  SaveOutlined,
  LoadingOutlined,
  HeartOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  BuildOutlined,
  UserSwitchOutlined,
  TeamOutlined,
  ScheduleOutlined,
  TagOutlined
} from '@ant-design/icons';
import { 
  useProviders, 
  useCreateProvider, 
  useUpdateProvider, 
  useDeleteProvider,
  useAddTagToProvider,
  useRemoveTagFromProvider,
  useTags,
  useGraphQLError,
  useDoctors,
  useClinics
} from '../hooks/useGraphQL';
import { useTranslation } from '../hooks/useTranslation';
import { getSpecializations, getSpecializationLabel } from '../constants/specializations';
import { getAddressString } from '../utils/address-utils';
import ProviderScheduleConfig from '../components/ProviderScheduleConfig';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;
const { TextArea } = Input;

interface Provider {
  id: string;
  doctor_id: string;
  clinic_id: string;
  consultation_fee?: number;
  department?: string;
  employment_type: string;
  start_date: Date;
  end_date?: Date;
  status: string;
  role?: string;
  specialization_at_clinic?: string;
  last_worked_at?: Date;
  verified_at?: Date;
  created_at: Date;
  updated_at: Date;
  doctor: {
    id: string;
    first_name_en: string;
    last_name_en: string;
    first_name_zh_hk: string;
    last_name_zh_hk: string;
    license_number: string;
    specialization: string;
    status: string;
    rating?: number;
    experience_years?: number;
    email: string;
    phone_number: string;
  };
  clinic: {
    id: string;
    name_en: string;
    name_zh_hk: string;
    internal_name: string;
    address: string;
    region: string;
    district: string;
    phone_number: string;
    email: string;
    status: string;
  };
  tags?: Array<{
    id: string;
    name: string;
    slug: string;
    category: string;
    status: string;
    display_name: string;
    description?: string;
    color: string;
    icon?: string;
    sort_order?: number;
    usage_count: number;
    last_used_at?: Date;
    is_system_tag: boolean;
  }>;
  schedules: Array<{
    id: string;
    status: string;
    max_appointments_per_day?: number;
    default_appointment_duration?: number;
    is_available_for_telemedicine?: boolean;
    is_available_for_walk_in?: boolean;
    schedule?: {
      monday?: Array<{ from: string; to: string }>;
      tuesday?: Array<{ from: string; to: string }>;
      wednesday?: Array<{ from: string; to: string }>;
      thursday?: Array<{ from: string; to: string }>;
      friday?: Array<{ from: string; to: string }>;
      saturday?: Array<{ from: string; to: string }>;
      sunday?: Array<{ from: string; to: string }>;
    };
  }>;
}

const Providers: React.FC = () => {
  const { t, currentLanguage } = useTranslation();
  const { data, loading, error } = useProviders();
  const { data: doctorsData, loading: doctorsLoading } = useDoctors();
  const { data: clinicsData, loading: clinicsLoading } = useClinics();
  const [createProvider] = useCreateProvider();
  const [updateProvider] = useUpdateProvider();
  const [deleteProvider] = useDeleteProvider();
  const [addTagToProvider] = useAddTagToProvider();
  const [removeTagFromProvider] = useRemoveTagFromProvider();
  const { handleError } = useGraphQLError();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [specializationFilter, setSpecializationFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  // Get specializations from centralized configuration
  const specializations = getSpecializations(currentLanguage);

  const employmentTypes = ['full_time', 'part_time', 'contract', 'consultant', 'locum'];

  const providers = data?.providers || [];
  const doctors = doctorsData?.doctors || [];
  const clinics = clinicsData?.clinics || [];

  // Throttle search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredProviders = providers.filter((provider: Provider) => {
    const matchesSearch = 
      provider.doctor.first_name_en.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      provider.doctor.last_name_en.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      provider.doctor.first_name_zh_hk.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      provider.doctor.last_name_zh_hk.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      (provider.clinic.internal_name || provider.clinic.name_zh_hk || provider.clinic.name_en || '').toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      provider.clinic.name_en.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      provider.clinic.name_zh_hk.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      provider.doctor.license_number.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      provider.doctor.specialization.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
    
    // Hide inactive providers by default - only show when explicitly filtered
    const matchesStatus = statusFilter === 'all' 
      ? provider.status !== 'inactive'  // Exclude inactive when showing all
      : provider.status === statusFilter;
    const matchesSpecialization = specializationFilter === 'all' || provider.doctor.specialization === specializationFilter;
    
    return matchesSearch && matchesStatus && matchesSpecialization;
  });

  const handleCreateProvider = async (values: any) => {
    try {
      setIsSubmitting(true);
      
      // Convert DatePicker values to ISO 8601 strings
      const formattedValues = {
        ...values,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : undefined,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : undefined,
        tenant: 'default'
      };
      
      await createProvider({
        variables: {
          createProviderDto: formattedValues
        }
      });
      setShowCreateModal(false);
      form.resetFields();
      message.success(t('providers.providerAssignmentCreated'));
    } catch (error) {
      console.error('Error creating provider:', error);
      message.error(handleError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProvider = async (id: string, values: any) => {
    try {
      setIsSubmitting(true);
      
      // Convert DatePicker values to ISO 8601 strings
      const formattedValues = {
        ...values,
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : undefined,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : undefined
      };
      
      await updateProvider({
        variables: {
          id,
          updateProviderDto: formattedValues
        }
      });
      setEditingProvider(null);
      form.resetFields();
      message.success(t('providers.providerAssignmentUpdated'));
    } catch (error) {
      console.error('Error updating provider:', error);
      message.error(handleError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProvider = async (id: string) => {
    try {
      await deleteProvider({
        variables: { id }
      });
      message.success(t('providers.providerAssignmentDeleted'));
    } catch (error) {
      console.error('Error deleting provider:', error);
      message.error(handleError(error));
    }
  };

  const handleOpenScheduleConfig = (providerId: string) => {
    setSelectedProviderId(providerId);
    setShowScheduleModal(true);
  };

  const handleCloseScheduleConfig = () => {
    setShowScheduleModal(false);
    setSelectedProviderId(null);
  };

  const handleAddTag = async (providerId: string, tagId: string) => {
    try {
      await addTagToProvider({
        variables: {
          providerId,
          tagId
        }
      });
      message.success(t('providers.tagAdded'));
    } catch (error) {
      console.error('Error adding tag:', error);
      message.error(handleError(error));
    }
  };

  const handleRemoveTag = async (providerId: string, tagId: string) => {
    try {
      await removeTagFromProvider({
        variables: {
          providerId,
          tagId
        }
      });
      message.success(t('providers.tagRemoved'));
    } catch (error) {
      console.error('Error removing tag:', error);
      message.error(handleError(error));
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'success', text: t('providers.active'), icon: <CheckCircleOutlined /> },
      inactive: { color: 'warning', text: t('common.inactive'), icon: <ExclamationCircleOutlined /> },
      on_leave: { color: 'error', text: t('providers.onLeave'), icon: <StopOutlined /> }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={config.color as any} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getEmploymentTypeTag = (type: string) => {
    const typeConfig = {
      full_time: { color: 'blue', text: t('providers.fullTime') },
      part_time: { color: 'green', text: t('providers.partTime') },
      contract: { color: 'orange', text: t('providers.contract') },
      consultant: { color: 'purple', text: t('providers.consultant') },
      locum: { color: 'cyan', text: t('providers.locum') }
    };
    const config = typeConfig[type as keyof typeof typeConfig] || { color: 'default', text: type };
    return (
      <Tag color={config.color as any}>
        {config.text}
      </Tag>
    );
  };

  const getWorkingDays = (schedules: Provider['schedules']) => {
    if (!schedules || schedules.length === 0) return t('providers.noSchedule');
    
    const activeSchedule = schedules.find(s => s.status === 'active');
    if (!activeSchedule || !activeSchedule.schedule) return t('providers.noSchedule');
    
    const days = Object.entries(activeSchedule.schedule).filter(([_, day]) => day && Array.isArray(day) && day.length > 0);
    return days.map(([day, slots]) => {
      const dayName = t(`days.${day}`);
      const timeSlots = Array.isArray(slots) ? slots.map(slot => `${slot.from}-${slot.to}`).join(', ') : '';
      return `${dayName}: ${timeSlots}`;
    }).join(', ');
  };

  const columns = [
    {
      title: t('providers.doctor'),
      key: 'doctor',
      render: (provider: Provider) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40}
            style={{ backgroundColor: '#1890ff' }}
            icon={<UserOutlined />}
          />
          <div style={{ marginLeft: 12 }}>
            <div style={{ fontWeight: 500 }}>
              {provider.doctor.first_name_en} {provider.doctor.last_name_en}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {provider.doctor.first_name_zh_hk} {provider.doctor.last_name_zh_hk}
            </Text>
            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
              {t('providers.license')}: {provider.doctor.license_number}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('providers.clinic'),
      key: 'clinic',
      render: (provider: Provider) => {
        
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BuildOutlined style={{ marginRight: 8, color: '#8c8c8c' }} />
            <div>
              <div style={{ fontWeight: 500 }}>
                {provider.clinic.internal_name || provider.clinic.name_zh_hk || provider.clinic.name_en}
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {provider.clinic.name_zh_hk}
              </Text>
              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                {getAddressString(provider.clinic, currentLanguage)}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: t('providers.specialization'),
      key: 'specialization',
      render: (provider: Provider) => {
        return (
          <Tag color="blue" icon={<MedicineBoxOutlined />}>
            {getSpecializationLabel(provider.doctor.specialization, currentLanguage)}
          </Tag>
        );
      },
    },
    {
      title: t('providers.tags'),
      key: 'tags',
      render: (provider: Provider) => {
        if (!provider.tags || provider.tags.length === 0) {
          return <Text type="secondary" style={{ fontSize: '12px' }}>{t('providers.noTags')}</Text>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {provider.tags.map(tag => (
              <Tag 
                key={tag.id} 
                color={tag.color || 'default'}
                style={{ margin: 0 }}
              >
                {tag.display_name || tag.name}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: t('common.status'),
      key: 'status',
      render: (provider: Provider) => getStatusTag(provider.status),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (provider: Provider) => (
        <Space>
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingProvider(provider);
                form.setFieldsValue({
                  ...provider,
                  start_date: provider.start_date ? dayjs(provider.start_date) : null,
                  end_date: provider.end_date ? dayjs(provider.end_date) : null
                });
              }}
            />
          </Tooltip>
          <Tooltip title={t('schedule.configureSchedule')}>
            <Button
              type="text"
              icon={<ScheduleOutlined />}
              onClick={() => handleOpenScheduleConfig(provider.id)}
            />
          </Tooltip>
          <Tooltip title={t('common.view')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
            />
          </Tooltip>
          <Popconfirm
            title={t('providers.deleteConfirmTitle')}
            onConfirm={() => handleDeleteProvider(provider.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('common.delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('providers.loadingProviders')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>{t('providers.title')}</Title>
            <Text type="secondary">{t('providers.subtitle')}</Text>
          </Col>
        </Row>
        
        <Card>
          <Alert
            message={t('providers.errorLoadingProviders')}
            description={handleError(error)}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                {t('providers.retry')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const activeProviders = providers.filter((p: Provider) => p.status === 'active').length;
  const onLeaveProviders = providers.filter((p: Provider) => p.status === 'on_leave').length;
  const fullTimeProviders = providers.filter((p: Provider) => p.employment_type === 'full_time').length;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>{t('providers.title')}</Title>
          <Text type="secondary">{t('providers.subtitle')}</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setShowCreateModal(true);
              form.resetFields();
            }}
          >
            {t('providers.addProvider')}
          </Button>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('providers.totalAssignments')}
              value={providers.length}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('providers.active')}
              value={activeProviders}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('providers.onLeave')}
              value={onLeaveProviders}
              prefix={<StopOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder={t('providers.searchProviders')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('common.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('providers.allStatus')}</Option>
              <Option value="active">{t('providers.active')}</Option>
              <Option value="inactive">{t('common.inactive')}</Option>
              <Option value="on_leave">{t('providers.onLeave')}</Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('providers.specialization')}
              value={specializationFilter}
              onChange={setSpecializationFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('providers.allSpecializations')}</Option>
              {specializations.map(spec => (
                <Option key={spec.key} value={spec.key}>{spec.value}</Option>
              ))}
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Button icon={<FilterOutlined />} style={{ width: '100%' }}>
              {t('providers.export')}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Providers Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredProviders}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <ProviderModal
        visible={showCreateModal || !!editingProvider}
        provider={editingProvider}
        doctors={doctors}
        clinics={clinics}
        specializations={specializations}
        employmentTypes={employmentTypes}
        isSubmitting={isSubmitting}
        form={form}
        onCancel={() => {
          setShowCreateModal(false);
          setEditingProvider(null);
          form.resetFields();
        }}
        onSubmit={(values) => {
          if (editingProvider) {
            handleUpdateProvider(editingProvider.id, values);
          } else {
            handleCreateProvider(values);
          }
        }}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
      />

      {/* Provider Schedule Configuration Modal */}
      {selectedProviderId && (
        <ProviderScheduleConfig
          providerId={selectedProviderId}
          visible={showScheduleModal}
          onClose={handleCloseScheduleConfig}
        />
      )}
    </div>
  );
};

interface ProviderModalProps {
  visible: boolean;
  provider?: Provider | null;
  doctors: any[];
  clinics: any[];
  specializations: Array<{ key: string; value: string }>;
  employmentTypes: string[];
  isSubmitting: boolean;
  form: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  onAddTag?: (providerId: string, tagId: string) => void;
  onRemoveTag?: (providerId: string, tagId: string) => void;
}

const ProviderModal: React.FC<ProviderModalProps> = ({ 
  visible, 
  provider, 
  doctors,
  clinics,
  specializations, 
  employmentTypes, 
  isSubmitting, 
  form, 
  onCancel, 
  onSubmit,
  onAddTag,
  onRemoveTag
}) => {
  const { t, currentLanguage } = useTranslation();
  const { data: tagsData, loading: tagsLoading } = useTags();
  const [selectedTagId, setSelectedTagId] = useState<string>('');
  
  // Get all available tags from the tags query
  const availableTags = React.useMemo(() => {
    if (!tagsData?.allTags) return [];
    
    return tagsData.allTags.map((tag: any) => ({
      id: tag.id,
      name: tag.name,
      display_name: tag.display_name || tag.name,
      color: tag.color || '#6B7280'
    }));
  }, [tagsData]);
  
  const providerTags = provider?.tags || [];
  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserSwitchOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {provider ? t('providers.editProviderAssignment') : t('providers.addNewProviderAssignment')}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          status: 'active',
          employment_type: 'full_time'
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('providers.doctor')}
              name="doctor_id"
              rules={[{ required: true, message: t('providers.selectDoctorRequired') }]}
            >
              <Select
                placeholder={t('providers.selectDoctor')}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
                dropdownStyle={{ maxHeight: 400 }}
                listHeight={400}
                optionLabelProp="label"
              >
                {doctors.map(doctor => (
                  <Option 
                    key={doctor.id} 
                    value={doctor.id}
                    label={`${doctor.first_name_en} ${doctor.last_name_en}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                      <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8 }} />
                      <div>
                        <div style={{ fontWeight: 500 }}>{doctor.first_name_en} {doctor.last_name_en}</div>
                        <small style={{ color: '#8c8c8c' }}>
                          {doctor.specialization} • {t('providers.license')}: {doctor.license_number}
                        </small>
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('providers.clinic')}
              name="clinic_id"
              rules={[{ required: true, message: t('providers.selectClinicRequired') }]}
            >
              <Select
                placeholder={t('providers.selectClinic')}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
                dropdownStyle={{ maxHeight: 400 }}
                listHeight={400}
                optionLabelProp="label"
              >
                {clinics.map(clinic => {
                  
                  return (
                    <Option 
                      key={clinic.id} 
                      value={clinic.id}
                      label={clinic.internal_name || clinic.name_zh_hk}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                        <BuildOutlined style={{ marginRight: 8, color: '#8c8c8c' }} />
                        <div>
                          <div style={{ fontWeight: 500 }}>{clinic.internal_name || clinic.name_zh_hk}</div>
                          <small style={{ color: '#8c8c8c' }}>
                            {getAddressString(clinic, currentLanguage)}
                          </small>
                        </div>
                      </div>
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label={t('providers.specializationAtClinic')}
              name="specialization_at_clinic"
            >
              <Select placeholder={t('providers.selectSpecialization')}>
                {specializations.map(spec => (
                  <Option key={spec.key} value={spec.key}>{spec.value}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('providers.employmentType')}
              name="employment_type"
              rules={[{ required: true, message: t('providers.selectEmploymentType') }]}
            >
              <Select>
                {employmentTypes.map(type => (
                  <Option key={type} value={type}>
                    {t(`providers.${type}`)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t('common.status')}
              name="status"
            >
              <Select>
                <Option value="active">{t('providers.active')}</Option>
                <Option value="inactive">{t('common.inactive')}</Option>
                <Option value="on_leave">{t('providers.onLeave')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('providers.startDate')}
              name="start_date"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('providers.endDate')}
              name="end_date"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('providers.department')}
              name="department"
            >
              <Input placeholder="e.g., Cardiology, Pediatrics" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('providers.role')}
          name="role"
        >
          <Input placeholder="e.g., Senior Consultant, Specialist" />
        </Form.Item>

        {provider && (
          <>
            <Divider orientation="left" style={{ marginTop: 24 }}>
              <TagOutlined style={{ marginRight: 8 }} />
              {t('providers.tags')}
            </Divider>
            
            <div style={{ marginBottom: 16 }}>
              <Space wrap>
                {providerTags.map(tag => (
                  <Tag
                    key={tag.id}
                    color={tag.color || 'default'}
                    closable
                    onClose={() => {
                      if (onRemoveTag && provider) {
                        onRemoveTag(provider.id, tag.id);
                      }
                    }}
                    style={{ marginBottom: 8 }}
                  >
                    {tag.display_name || tag.name}
                  </Tag>
                ))}
              </Space>
            </div>
            
            {availableTags.length > 0 && (
              <Form.Item label={t('providers.addTag')}>
                <Space style={{ width: '100%' }}>
                  <Select
                    style={{ width: 300 }}
                    placeholder={t('providers.selectTag')}
                    value={selectedTagId}
                    onChange={setSelectedTagId}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) || false
                    }
                  >
                    {availableTags
                      .filter((tag: any) => !providerTags.find((pt: any) => pt.id === tag.id))
                      .map((tag: any) => (
                        <Option key={tag.id} value={tag.id} label={tag.display_name}>
                          <Tag color={tag.color} style={{ marginRight: 8 }}>
                            {tag.display_name}
                          </Tag>
                        </Option>
                      ))}
                  </Select>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      if (selectedTagId && onAddTag && provider) {
                        onAddTag(provider.id, selectedTagId);
                        setSelectedTagId('');
                      }
                    }}
                    disabled={!selectedTagId}
                  >
                    {t('providers.addTag')}
                  </Button>
                </Space>
              </Form.Item>
            )}
          </>
        )}

        <div style={{ textAlign: 'right', marginTop: 24 }}>
          <Space>
            <Button onClick={onCancel} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isSubmitting}
              icon={<SaveOutlined />}
            >
              {provider ? t('providers.editProvider') : t('providers.addProvider')}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default Providers; 